// src/agent-client.ts
import { z } from "zod";
var CLIENT_VERSION = 2;
var PaymentRequiredError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PaymentRequiredError";
  }
};
async function getErrorForResponse(response) {
  if (response.ok) {
    return void 0;
  } else {
    const text = await response.text();
    if (response.status === 402) {
      return new PaymentRequiredError(`Agent: ${response.status} ${response.statusText}: ${text}`);
    }
    return new Error(`Agent: ${response.status} ${response.statusText}: ${text}`);
  }
}
var rowValueSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
var columnTypeSchema = z.enum(["string", "number", "boolean", "image-uri", "uri", "date", "date-time"]);
var rowSchema = z.record(
  z.string(),
  z.object({
    type: columnTypeSchema,
    description: z.string().optional(),
    displayName: z.string().optional()
    // defaults to the internal name
  })
);
var AUTHENTICATION_TIMEOUT_ERROR = "Authentication timeout";
var AgentClient = class {
  options;
  baseURL;
  _headersPromiseFetchTime = 0;
  _headersPromise = void 0;
  getHeaders() {
    const fifteenMinutes = 15 * 60 * 1e3;
    if (Date.now() > this._headersPromiseFetchTime + fifteenMinutes) {
      this._headersPromise = void 0;
    }
    if (this._headersPromise !== void 0) {
      return this._headersPromise;
    }
    const headerFn = async () => {
      const authTimeoutMs = this.options.authTimeoutMs ?? 1e4;
      const startTime = Date.now();
      try {
        const idToken = await Promise.race([
          this.options.getUserIdToken(),
          new Promise(
            (_, reject) => setTimeout(() => reject(new Error(AUTHENTICATION_TIMEOUT_ERROR)), authTimeoutMs)
          )
        ]);
        if (idToken === void 0)
          throw new Error("Failed to get current user");
        const headers = {
          ...this.options.headers,
          Authorization: `Bearer ${idToken}`
        };
        return headers;
      } catch (error) {
        if (error instanceof Error && error.message === AUTHENTICATION_TIMEOUT_ERROR) {
          this.recordTimeoutEvent("auth_timeout", Date.now() - startTime);
        }
        throw error;
      }
    };
    this._headersPromiseFetchTime = Date.now();
    this._headersPromise = headerFn();
    return this._headersPromise;
  }
  constructor(options) {
    this.options = options;
    const { endpoint, getGatewayURL } = this.options;
    const gatewayURL = getGatewayURL("agent-service");
    this.baseURL = new URL(endpoint.startsWith("/") ? endpoint.substring(1) : endpoint, `${gatewayURL}/agents/`).toString() + "/";
    void this.getHeaders();
  }
  recordTimeoutEvent(type, durationMs, runId) {
    console.warn(`Agent timeout: ${type}, duration: ${durationMs}ms`);
    this.options.onTimeout?.(type, durationMs, runId);
  }
  /**
   * Get next message suggestions that the agent can present to the user.
   *
   * @param count - The number of suggestions to get.
   * @returns A promise of the suggestions.
   */
  async getNextMessageSuggestions(req, count = 2) {
    const url = new URL("suggest", this.baseURL);
    const body = {
      ...req,
      count
    };
    try {
      const { data } = await this.post(url.toString(), body);
      return data;
    } catch {
      return [];
    }
  }
  /**
   * Get initial message suggestions that the agent can present to the user.
   *
   * @param count - The number of suggestions to get.
   * @returns A promise of the suggestions.
   */
  async getInitialNextMessageSuggestions(req, count = 2) {
    const url = new URL("suggest", this.baseURL);
    const body = {
      ...req,
      isInitial: true,
      count
    };
    try {
      const { data } = await this.post(url.toString(), body);
      return data;
    } catch {
      return [];
    }
  }
  async post(url, body) {
    const headers = await this.getHeaders();
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-glide-agent-client-version": CLIENT_VERSION.toString(),
          // TODO: Remove this once the agent service is deployed
          // without it.
          "x-glide-agent-support-concurrent": "true",
          ...headers
        },
        body: JSON.stringify(body)
      });
      const error = await getErrorForResponse(response);
      if (error !== void 0)
        throw error;
      return await response.json();
    } catch (err) {
      console.error("Agent", err);
      throw err;
    }
  }
  async generateScreen(args) {
    const { screenKind, userPrompt, table, userTable, runId } = args;
    const gatewayURL = this.options.getGatewayURL("agent-service");
    const url = new URL("screens/generate", `${gatewayURL}/`);
    const userId = await this.options.getUserIdToken();
    const body = {
      screenKind,
      table,
      userTable,
      userPrompt,
      runId,
      userId
    };
    const data = await this.post(url.toString(), body);
    const result = {
      components: data.components,
      newScreens: data.newScreens,
      newTemplates: data.newTemplates,
      newColumns: data.newColumns.map((c) => ({
        ...c,
        // This has a default cause we're deploying the client before the service.
        scope: c.scope ?? "table"
      }))
    };
    return result;
  }
  async editScreen(args) {
    const { screenKind, currentComponents, userPrompt, table, userTable, runId } = args;
    const gatewayURL = this.options.getGatewayURL("agent-service");
    const url = new URL("screens/edit", `${gatewayURL}/`);
    const userId = await this.options.getUserIdToken();
    const body = {
      screenKind,
      currentComponents,
      table,
      userTable,
      userPrompt,
      runId,
      userId
    };
    const data = await this.post(url.toString(), body);
    return data;
  }
  async sendFeedback({ comment, appID }) {
    const url = new URL("send-feedback", this.baseURL);
    const body = { comment, appID };
    const { data } = await this.post(url.toString(), body);
    return data;
  }
  async createEvalScenario(args) {
    const gatewayURL = this.options.getGatewayURL("agent-service");
    const url = new URL("evals/scenarios", `${gatewayURL}/`);
    await this.post(url.toString(), args);
  }
};

// src/types.ts
import { modelMessageSchema } from "ai";
import { z as z2 } from "zod/v4";
var toolCallSchema = z2.object({
  type: z2.literal("function_call"),
  name: z2.string(),
  call_id: z2.string(),
  arguments: z2.string(),
  display_name: z2.string().optional(),
  add_to_history: z2.boolean().optional(),
  // default: true
  log_id: z2.string().optional()
  // for humanloop
});
var tableRowSchema = z2.record(z2.string(), z2.unknown());
var textContentSchema = z2.object({
  type: z2.literal("text"),
  text: z2.string()
});
var tableContentSchema = z2.object({
  type: z2.literal("table"),
  rows: z2.array(tableRowSchema)
});
var qrCodeContentSchema = z2.object({
  type: z2.literal("qr-code"),
  url: z2.string().describe("The url to display as a qr code"),
  additionalMessage: z2.string().describe("An optional message to display with the qr code").optional()
});
var editAppThemeContentSchema = z2.object({
  type: z2.literal("edit_app_theme")
});
var importDataContentSchema = z2.object({
  type: z2.literal("import_data")
});
var rollbackAppSchema = z2.object({
  type: z2.literal("rollback_app"),
  version: z2.number().describe("The version to rollback to")
});
var upgradePromptContentSchema = z2.object({
  type: z2.literal("upgrade_prompt"),
  title: z2.string().describe("The title of the upgrade prompt").optional(),
  message: z2.string().describe("The message to display in the upgrade prompt").optional()
});
var toolRichContentSchema = z2.union([
  textContentSchema,
  tableContentSchema,
  qrCodeContentSchema,
  editAppThemeContentSchema,
  importDataContentSchema,
  rollbackAppSchema,
  upgradePromptContentSchema
]);
var frontendEffectSchema = z2.object({
  name: z2.string(),
  arguments: z2.unknown()
});
var toolOutputSchema = z2.object({
  llmMessage: z2.string().describe("Passed to parent agent"),
  richContent: z2.array(toolRichContentSchema).describe("Maybe shown on the UI. Parent agent does not see this"),
  error: z2.boolean().optional(),
  frontendEffect: frontendEffectSchema.optional()
});
var toolCallResultSchema = z2.object({
  type: z2.literal("function_call_output"),
  call_id: z2.string(),
  output: toolOutputSchema,
  add_to_history: z2.boolean().optional(),
  // default: true
  log_id: z2.string().optional()
  // for humanloop
});
var draftStateSchema = z2.object({
  state: z2.literal("draft")
});
var publishedStateSchema = z2.object({
  state: z2.literal("published"),
  url: z2.string()
});
var publishedAppSchema = z2.union([draftStateSchema, publishedStateSchema]);
var turnContextSchema = z2.object({
  mode: z2.string().optional(),
  currentScreenName: z2.string().optional(),
  // This is now only used for internal purposes and should not be sent from the frontend.
  extraContext: z2.record(z2.string(), z2.unknown()).optional(),
  client: z2.object({
    formFactor: z2.enum(["large", "mobile"])
  }).optional(),
  user: z2.object({
    displayName: z2.string(),
    distinctId: z2.string().optional().describe(
      "The unique identifier for the user for analytics (e.g. should be the same as the distinct_id passed to PostHog to identify the user)"
    ),
    emails: z2.array(z2.string()),
    appUserID: z2.string().optional().describe("Only needed when running in the player (e.g. data studio)")
  }).optional(),
  org: z2.object({
    displayName: z2.string(),
    id: z2.string().optional()
  }).optional(),
  app: z2.object({
    appID: z2.string(),
    name: z2.string(),
    description: z2.string().optional(),
    publishState: publishedAppSchema.optional()
  })
});
var turnRequestSchema = z2.object({
  runId: z2.string().optional(),
  context: turnContextSchema,
  history: z2.array(modelMessageSchema).min(1)
});
var agentUsageSchema = z2.object({
  inputTokens: z2.number(),
  outputTokens: z2.number(),
  model: z2.string(),
  tool: z2.string().optional()
});
var agentEventMetadataSchema = z2.object({
  usage: z2.array(agentUsageSchema),
  agentUsageUpgradeStrategy: z2.string().optional()
});

// src/tool-output.ts
function isToolOutput(obj) {
  return typeof obj === "object" && obj !== null && "llmMessage" in obj && typeof obj.llmMessage === "string" && "richContent" in obj && Array.isArray(obj.richContent);
}
function generateOutput(generator) {
  const llmMessages = [];
  const richContent = [];
  for (const content of generator()) {
    if (typeof content === "string") {
      llmMessages.push(content);
    } else {
      richContent.push(...content.richContent);
      llmMessages.push(content.llmMessage);
    }
  }
  return { richContent, llmMessage: llmMessages.join("\n") };
}
function textFromMaybeTemplate(strings, ...values) {
  if (typeof strings === "string")
    return strings;
  let fullText = "";
  strings.forEach((string, i) => {
    fullText += string;
    if (i < values.length) {
      fullText += values[i];
    }
  });
  const text = fullText.trim();
  return text;
}
function textOutput(strings, ...values) {
  const text = textFromMaybeTemplate(strings, ...values);
  return {
    richContent: [{ type: "text", text }],
    llmMessage: text
  };
}
function messageOutput(strings, ...values) {
  const text = textFromMaybeTemplate(strings, ...values);
  return {
    richContent: [],
    llmMessage: text
  };
}
function errorOutput(strings, ...values) {
  const text = textFromMaybeTemplate(strings, ...values);
  return {
    richContent: [],
    llmMessage: text,
    error: true
  };
}

// src/use-chat.ts
import { useChat as useVercelChat } from "@ai-sdk/react";
import {
  convertToModelMessages,
  DefaultChatTransport as VercelDefaultChatTransport
} from "ai";
import { getToolName } from "ai";
var DefaultChatTransport = class extends VercelDefaultChatTransport {
  constructor(options) {
    super({
      ...options,
      headers: async () => ({
        "x-glide-agent-support-concurrent": "true",
        "x-glide-agent-client-version": CLIENT_VERSION.toString(),
        ...await options.headers
      })
    });
  }
};
function glideUIMessagesToModelMessages(messages) {
  return convertToModelMessages(messages);
}
function useChat(options) {
  return useVercelChat(options);
}
export {
  AgentClient as Agent,
  CLIENT_VERSION,
  DefaultChatTransport,
  PaymentRequiredError,
  agentEventMetadataSchema,
  agentUsageSchema,
  columnTypeSchema,
  editAppThemeContentSchema,
  errorOutput,
  frontendEffectSchema,
  generateOutput,
  getToolName,
  glideUIMessagesToModelMessages,
  importDataContentSchema,
  isToolOutput,
  messageOutput,
  qrCodeContentSchema,
  rollbackAppSchema,
  rowSchema,
  rowValueSchema,
  tableContentSchema,
  tableRowSchema,
  textContentSchema,
  textOutput,
  toolCallResultSchema,
  toolCallSchema,
  toolOutputSchema,
  toolRichContentSchema,
  turnContextSchema,
  turnRequestSchema,
  upgradePromptContentSchema,
  useChat
};
